import "./cancel-order-button.scss";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import React, { FC } from "react";
import { CancelOrderModal } from "./CancelOrderModal";
import {
  CancellationReason,
  CancellationState,
  OrderDetails,
  isCancellableStatus,
  isHopCancellable,
  shouldHideCancelButton,
} from "./types";
import { OrderItem } from "@/pages/orders/orders.types";

const ButtonWithTooltip = withTooltip()(Button);

interface CancelOrderButtonProps {
  orderStatus?: string;
  orderId: string;
  orderDetails: OrderDetails;
  cancellationState: CancellationState;
  onConfirmCancellation: (reason: CancellationReason) => void;
  orderItems?: OrderItem[];
}

export const CancelOrderButton: FC<CancelOrderButtonProps> = ({
  orderStatus,
  orderId,
  orderDetails,
  cancellationState,
  onConfirmCancellation,
  orderItems,
}) => {
  if (shouldHideCancelButton(orderStatus)) {
    return null;
  }

  const isCancellable = isCancellableStatus(orderStatus);
  const hopCancellable = isHopCancellable(orderItems);

  if (!isCancellable || !hopCancellable) {
    return (
      <ButtonWithTooltip
        variant={ButtonVariant.Secondary}
        disabled
        aria-label={`Cancel order ${orderId}`}
        data-testid="cancel-order-button"
        tooltipText={!isCancellable ? "This order can no longer be cancelled" : "Order is being fulfilled and cannot be cancelled"}
        className="cancel-order-button"
      >
        Cancel order
      </ButtonWithTooltip>
    );
  }

  const renderModal = ({ close }: RenderModalFunctionParams) => (
    <CancelOrderModal
      onClose={close}
      onConfirm={(reason: CancellationReason) => {
        onConfirmCancellation(reason);
      }}
      orderDetails={orderDetails}
      cancellationState={cancellationState}
    />
  );

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <Button
          variant={ButtonVariant.Secondary}
          onClick={open}
          aria-label={`Cancel order ${orderId}`}
          data-testid="cancel-order-button"
          className="cancel-order-button"
        >
          Cancel order
        </Button>
      )}
    </WithModal>
  );
};
